// Support resources catalog — Employee Portal & App spec §15.
//
// Approved support resources the employee can browse from both the Employee
// Portal and Maeve. A resource is either a PLATFORM DEFAULT (organizationId
// NULL — Samaritans, Cruse, Mind, NHS 111, seeded for every tenant) or an
// ORG-SPECIFIC resource an HR Admin has added (EAP, occupational health,
// internal wellbeing lead). HR creating an org resource IS its approval — this
// catalog is org-authored content, not Maeve prompt content, so it does not go
// through the CGC gate. Platform defaults are pre-approved and read-only to HR.
//
// Privacy note (§19.2): the catalog is browse-only. We deliberately do NOT add
// per-resource usage analytics — surfacing "who viewed what" reads as
// surveillance. The existing append-only `resource_access_events` log stays
// scoped to crisis-link clicks for the safety handoff, nothing more.

import { z } from 'zod';

// §15.1 resource categories.
export const RESOURCE_CATEGORIES = [
  'internal',
  'bereavement',
  'mental_health',
  'crisis',
  'workplace',
  'practical',
] as const;
export type ResourceCategory = (typeof RESOURCE_CATEGORIES)[number];

export const RESOURCE_CATEGORY_LABELS: Record<ResourceCategory, string> = {
  internal: 'Internal support',
  bereavement: 'Bereavement support',
  mental_health: 'Mental health support',
  crisis: 'Crisis support',
  workplace: 'Workplace support',
  practical: 'Practical support',
};

// A resource as the employee sees it — the §15.2 presentation fields.
export interface ResourceCard {
  id: string;
  category: ResourceCategory;
  name: string;
  // Short description (§15.2).
  description: string;
  // "When to use it" (§15.2) — optional.
  whenToUse: string | null;
  // "Contact link or phone number" (§15.2) — either, both, or neither.
  contactUrl: string | null;
  contactPhone: string | null;
  // "Whether it is internal or external" (§15.2).
  isExternal: boolean;
  // "Whether it is confidential" (§15.2).
  isConfidential: boolean;
  // true = a platform default (organizationId NULL). The employee UI ignores
  // this; the HR management surface uses it to render defaults as read-only.
  isDefault: boolean;
}

// Stable display order: by §15.1 category, then alphabetically by name. Pure +
// reused by the employee view and the HR management list so both render the
// catalog identically.
export function sortResourceCards(cards: ResourceCard[]): ResourceCard[] {
  return [...cards].sort((a, b) => {
    const byCat = RESOURCE_CATEGORIES.indexOf(a.category) - RESOURCE_CATEGORIES.indexOf(b.category);
    return byCat !== 0 ? byCat : a.name.localeCompare(b.name);
  });
}

// Trim-then-null: an empty/whitespace optional field stores as NULL, never "".
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => v || null);

// HR-authored org resource — create. Platform defaults are not created via the
// API (they are seeded in a migration), so this always writes an org-scoped row.
export const CreateResourceRequestSchema = z
  .object({
    category: z.enum(RESOURCE_CATEGORIES),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(400),
    whenToUse: optionalText(400),
    // A loose URL check — `z.url()` rejects intranet hosts/`tel:` which an org
    // resource may legitimately use, so we only require a non-empty trimmed
    // string and cap the length.
    contactUrl: optionalText(500),
    contactPhone: optionalText(60),
    isExternal: z.boolean(),
    isConfidential: z.boolean(),
  })
  .strict();
export type CreateResourceRequest = z.infer<typeof CreateResourceRequestSchema>;

// Update is a full replace of the editable fields (PUT semantics).
export const UpdateResourceRequestSchema = CreateResourceRequestSchema;
export type UpdateResourceRequest = z.infer<typeof UpdateResourceRequestSchema>;
